import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ProviderDisagreementsService, {
    Coordinate,
    DeliveredCoordinate,
    NearestToDelivery,
    ProviderDisagreementRow,
    ProviderDisagreementsQueryParams,
    ProviderDisagreementsSummary,
    UberSidedWith
} from '../../services/providerDisagreementsService';
import AddressPagination from '../AddressManagement/AddressPagination';
import CoordinatesComparisonMapModal from '../AddressManagement/CoordinatesComparisonMapModal';
import { PodMarker } from '../AddressManagement/ComparisonMap';
import PodsService from '../../services/podsService';
import { US_STATES } from '../../data/usStates';

const PAGE_SIZE = 50;

const PROVIDER_COLORS: Record<string, string> = {
    GOOGLE: '#34a851',
    RADAR: '#000257',
    UBER: '#000000',
    DELIVERED: '#6f42c1'
};

const CoordinateCell: React.FC<{ coordinate: Coordinate | DeliveredCoordinate | null | undefined }> = ({ coordinate }) => {
    if (!coordinate) {
        return <span className="text-muted small">-</span>;
    }

    const text = `${coordinate.lat.toFixed(5)}, ${coordinate.lng.toFixed(5)}`;

    return (
        <div className="d-flex align-items-center gap-1">
            <small className="font-monospace">{text}</small>
            <button
                className="btn btn-link btn-sm p-0 border-0 text-muted"
                onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(text);
                }}
                title="Copy coordinates to clipboard"
            >
                <i className="bi bi-clipboard" style={{ fontSize: '0.8em' }}></i>
            </button>
            {'podCount' in coordinate && (
                <span className="badge bg-light text-dark border" title="Number of PODs behind this point">
                    x{coordinate.podCount}
                </span>
            )}
        </div>
    );
};

const SidedWithBadge: React.FC<{ value: UberSidedWith }> = ({ value }) => {
    switch (value) {
        case 'GOOGLE':
            return <span className="badge" style={{ backgroundColor: PROVIDER_COLORS.GOOGLE }}>Google</span>;
        case 'RADAR':
            return <span className="badge" style={{ backgroundColor: PROVIDER_COLORS.RADAR }}>Radar</span>;
        case 'BOTH':
            return <span className="badge bg-dark">Both</span>;
        case 'NEITHER':
            return <span className="badge bg-danger">Neither</span>;
        default:
            return <span className="badge bg-secondary">No Uber</span>;
    }
};

const NearestBadge: React.FC<{ value: NearestToDelivery }> = ({ value }) => {
    switch (value) {
        case 'GOOGLE':
            return <span className="badge" style={{ backgroundColor: PROVIDER_COLORS.GOOGLE }}>Google</span>;
        case 'RADAR':
            return <span className="badge" style={{ backgroundColor: PROVIDER_COLORS.RADAR }}>Radar</span>;
        case 'UBER':
            return <span className="badge" style={{ backgroundColor: PROVIDER_COLORS.UBER }}>Uber</span>;
        default:
            return <span className="badge bg-secondary">No POD</span>;
    }
};

const UberWasRightIcon: React.FC<{ value: boolean | null }> = ({ value }) => {
    if (value === null) {
        return <span className="text-muted">-</span>;
    }
    return value
        ? <i className="bi bi-check-circle-fill text-success" title="Uber's side matched the nearest-to-delivery provider"></i>
        : <i className="bi bi-x-circle-fill text-danger" title="Uber's side did not match the nearest-to-delivery provider"></i>;
};

const DEFAULT_FILTERS: ProviderDisagreementsQueryParams = {
    from: undefined,
    to: undefined,
    stateCode: undefined,
    zip: undefined,
    onlyDisagreements: true,
    onlyWithPods: false
};

const ProviderDisagreementsPage: React.FC = () => {
    const [rows, setRows] = useState<ProviderDisagreementRow[]>([]);
    const [summary, setSummary] = useState<ProviderDisagreementsSummary | null>(null);
    const [totalItems, setTotalItems] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Draft filter values, bound to the form inputs - not applied until "Search" is clicked
    const [draftFrom, setDraftFrom] = useState('');
    const [draftTo, setDraftTo] = useState('');
    const [draftStateCode, setDraftStateCode] = useState('');
    const [draftZip, setDraftZip] = useState('');
    const [draftOnlyDisagreements, setDraftOnlyDisagreements] = useState(true);
    const [draftOnlyWithPods, setDraftOnlyWithPods] = useState(false);

    // Last-applied filters, used by Refresh and pagination so they repeat the same search
    const [appliedFilters, setAppliedFilters] = useState<ProviderDisagreementsQueryParams>(DEFAULT_FILTERS);

    const [mapRow, setMapRow] = useState<ProviderDisagreementRow | null>(null);
    const [mapRowPodMarkers, setMapRowPodMarkers] = useState<PodMarker[]>([]);

    useEffect(() => {
        if (!mapRow?.traceId) {
            setMapRowPodMarkers([]);
            return;
        }

        let cancelled = false;
        PodsService.getPODsByTraceId(mapRow.traceId)
            .then((response) => {
                if (cancelled) return;
                const data = Array.isArray(response.data) ? response.data : [];
                const markers: PodMarker[] = data
                    .filter((pod: any) => pod.latitude != null && pod.longitude != null)
                    .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map((pod: any) => ({
                        id: pod.id,
                        lat: pod.latitude,
                        lng: pod.longitude,
                        podUrl: pod.podUrl,
                        trackingNumber: pod.trackingNumber,
                        createdAt: pod.createdAt
                    }));
                setMapRowPodMarkers(markers);
            })
            .catch(() => {
                if (!cancelled) setMapRowPodMarkers([]);
            });

        return () => {
            cancelled = true;
        };
    }, [mapRow]);

    const runQuery = useCallback(async (targetPage: number, filters: ProviderDisagreementsQueryParams) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await ProviderDisagreementsService.getProviderDisagreements({
                ...filters,
                page: targetPage,
                pageSize: PAGE_SIZE
            });
            setRows(response.items);
            setSummary(response.summary);
            setTotalItems(response.totalItems);
            setPage(response.page);
        } catch (err) {
            setError('Failed to load the report. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        runQuery(1, DEFAULT_FILTERS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        const filters: ProviderDisagreementsQueryParams = {
            from: draftFrom || undefined,
            to: draftTo || undefined,
            stateCode: draftStateCode || undefined,
            zip: draftZip || undefined,
            onlyDisagreements: draftOnlyDisagreements,
            onlyWithPods: draftOnlyWithPods
        };
        setAppliedFilters(filters);
        runQuery(1, filters);
    };

    const handleRefresh = () => {
        runQuery(page, appliedFilters);
    };

    const handlePageChange = (targetPage: number) => {
        runQuery(targetPage, appliedFilters);
    };

    const totalPages = Math.ceil(totalItems / PAGE_SIZE);

    const formatDate = (value: string) => new Date(value).toLocaleDateString();

    return (
        <div className="container-fluid py-4 pb-5 mb-5">
            <div className="mb-4">
                <h2 className="fw-bold mb-1">Provider Disagreements</h2>
                <p className="text-muted mb-0">Compare Google, Radar and Uber coordinates against each other and against where the package was actually delivered.</p>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                </div>
            )}

            <div className="card shadow-sm mb-3">
                <div className="card-body d-flex flex-wrap align-items-end gap-3">
                    <div>
                        <label className="form-label small text-muted mb-1">From</label>
                        <input type="date" className="form-control form-control-sm" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} />
                    </div>
                    <div>
                        <label className="form-label small text-muted mb-1">To</label>
                        <input type="date" className="form-control form-control-sm" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} />
                    </div>
                    <div>
                        <label className="form-label small text-muted mb-1">State</label>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: '160px' }}
                            value={draftStateCode}
                            onChange={(e) => setDraftStateCode(e.target.value)}
                        >
                            <option value="">All states</option>
                            {US_STATES.map((state) => (
                                <option key={state.code} value={state.code}>{state.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="form-label small text-muted mb-1">Zip</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            style={{ width: '110px' }}
                            placeholder="60612"
                            value={draftZip}
                            onChange={(e) => setDraftZip(e.target.value)}
                        />
                    </div>
                    <div className="form-check form-switch mb-1">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="onlyDisagreementsSwitch"
                            checked={draftOnlyDisagreements}
                            onChange={(e) => setDraftOnlyDisagreements(e.target.checked)}
                        />
                        <label className="form-check-label small" htmlFor="onlyDisagreementsSwitch">
                            Only disagreements
                        </label>
                    </div>
                    <div className="form-check form-switch mb-1">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="onlyWithPodsSwitch"
                            checked={draftOnlyWithPods}
                            onChange={(e) => setDraftOnlyWithPods(e.target.checked)}
                        />
                        <label className="form-check-label small" htmlFor="onlyWithPodsSwitch">
                            Only deliveries with PODs
                        </label>
                    </div>
                    <div className="d-flex gap-2 ms-auto">
                        <button className="btn btn-primary btn-sm" onClick={handleSearch} disabled={isLoading}>
                            <i className="bi bi-search me-1"></i>
                            Search
                        </button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={handleRefresh} disabled={isLoading}>
                            <i className={`${isLoading ? 'spinner-border spinner-border-sm' : 'bi bi-arrow-clockwise'} me-1`}></i>
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {summary && summary.totalRows > 0 && (
                <div className="alert alert-light border small mb-3">
                    Uber sided with Radar in <strong>{summary.pctSidedRadar}%</strong>, Google in <strong>{summary.pctSidedGoogle}%</strong>, neither in <strong>{summary.pctSidedNeither}%</strong>.
                    {' '}Where delivered, Uber's side was correct in <strong>{summary.pctUberWasRight}%</strong>.
                </div>
            )}

            <div className="table-responsive shadow-sm border rounded">
                <table className="table table-hover table-striped mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th scope="col">Address Id</th>
                            <th scope="col">Full Address</th>
                            <th scope="col">Date</th>
                            <th scope="col">State</th>
                            <th scope="col">Google</th>
                            <th scope="col">Radar</th>
                            <th scope="col">Uber</th>
                            <th scope="col">Delivered</th>
                            <th scope="col" className="text-center">Uber sided with</th>
                            <th scope="col" className="text-center">Nearest to delivery</th>
                            <th scope="col" className="text-center">Uber was right</th>
                            <th scope="col" className="text-center">Map</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={12} className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={12} className="text-center py-5">
                                    <i className="bi bi-inbox fs-1 text-muted opacity-50"></i>
                                    <p className="mt-2 text-muted">No rows matching your filters.</p>
                                </td>
                            </tr>
                        ) : (
                            rows.map((row) => (
                                <tr key={row.addressId}>
                                    <td>
                                        {row.traceId ? (
                                            <Link to={`/address-management/${row.traceId}`} className="text-decoration-none">
                                                #{row.addressId}
                                            </Link>
                                        ) : (
                                            <span>#{row.addressId}</span>
                                        )}
                                    </td>
                                    <td className="small" style={{ maxWidth: '260px', wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.fullAddress || '-'}</td>
                                    <td className="small text-muted">{formatDate(row.createdAt)}</td>
                                    <td className="small">{row.stateCode || '-'}</td>
                                    <td><CoordinateCell coordinate={row.google} /></td>
                                    <td><CoordinateCell coordinate={row.radar} /></td>
                                    <td><CoordinateCell coordinate={row.uber} /></td>
                                    <td><CoordinateCell coordinate={row.delivered} /></td>
                                    <td className="text-center"><SidedWithBadge value={row.uberSidedWith} /></td>
                                    <td className="text-center"><NearestBadge value={row.nearestToDelivery} /></td>
                                    <td className="text-center"><UberWasRightIcon value={row.uberWasRight} /></td>
                                    <td className="text-center">
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setMapRow(row)}
                                            title="Preview on the map"
                                        >
                                            <i className="bi bi-map"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <AddressPagination
                currentPage={page}
                totalPages={totalPages}
                pageSize={PAGE_SIZE}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                isLoading={isLoading}
            />

            {mapRow && (
                <CoordinatesComparisonMapModal
                    show={!!mapRow}
                    onClose={() => setMapRow(null)}
                    traceId={mapRow.traceId || undefined}
                    fullAddress={`Address #${mapRow.addressId}${mapRow.stateCode ? `, ${mapRow.stateCode}` : ''}${mapRow.zip ? ` ${mapRow.zip}` : ''}`}
                    googleCoordinates={mapRow.google}
                    radarCoordinates={mapRow.radar}
                    uberCoordinates={mapRow.uber}
                    deliveredCoordinates={mapRow.delivered}
                    podMarkers={mapRowPodMarkers}
                />
            )}
        </div>
    );
};

export default ProviderDisagreementsPage;
